import React, { useState } from "react";
import {
  UserCheck,
  MonitorCheck,
  Video,
  Clock,
  Sparkles,
  Users,
  ListChecks,
  SlidersHorizontal,
  Check,
  X,
  ChevronDown,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, SectionHead, Card, Badge } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { PageHero, FeatureCard, CTASection, VideoTile, WhatsAppButton } from "../../components/marketing/Shared";

function ReviewMockup() {
  return (
    <div className="rounded-2xl border border-white/10 bg-neutral-900/80 p-3 shadow-2xl">
      <div className="grid grid-cols-3 gap-1.5">
        <div className="col-span-2">
          <VideoTile label="screen" />
        </div>
        <VideoTile label="camera" alert />
      </div>
      <div className="mt-2 rounded-lg bg-black/40 p-2">
        <div className="relative h-2 w-full rounded-full bg-white/10">
          {[18, 34, 52, 71, 88].map((p, i) => (
            <span
              key={i}
              className="absolute top-1/2 h-3 w-1 -translate-y-1/2 rounded-full"
              style={{ left: `${p}%`, background: i === 3 ? "hsl(var(--destructive))" : "hsl(var(--warning))" }}
            />
          ))}
          <span className="absolute left-[45%] top-1/2 h-4 w-0.5 -translate-y-1/2 bg-brand" />
        </div>
        <div className="mt-1.5 flex justify-between font-mono text-[9px] text-white/40">
          <span>00:00</span>
          <span>server-clock synced</span>
          <span>01:30:00</span>
        </div>
      </div>
    </div>
  );
}

export default function Exams() {
  const { t } = useI18n();
  const [faq, setFaq] = useState(0);

  const features = [
    { icon: UserCheck, k: "identity" },
    { icon: MonitorCheck, k: "preflight" },
    { icon: Video, k: "capture" },
    { icon: Clock, k: "timestamp" },
    { icon: Sparkles, k: "ai" },
    { icon: Users, k: "modes" },
    { icon: ListChecks, k: "review" },
    { icon: SlidersHorizontal, k: "rules" },
  ];

  const compareRows = [
    ["Identity verification", false, true],
    ["Tamper-proof server timestamp", false, true],
    ["AI event detection", false, true],
    ["Gap-free recording", false, true],
    ["Per-candidate integrity report", false, true],
    ["Scales to large simultaneous volumes", false, true],
  ];

  const faqs = [
    ["Do candidates need to install anything?", "No — capture runs entirely in the browser over WebSocket/WebCodecs, cross-OS and cross-browser."],
    ["How is evidence protected against tampering?", "Every frame is stamped with the server clock, never the client’s. Recordings are gap-free and stored raw before any overlay or encode."],
    ["Can I run live and recorded exams?", "Yes — live (a proctor monitors a grid), recorded (review later) or hybrid, configurable per exam."],
    ["Is it LGPD/GDPR compliant?", "Yes. Monitored users get a clear recording notice, and you control retention and export."],
  ];

  return (
    <>
      <PageHero
        breadcrumb={t("nav.exams")}
        accent="exams"
        eyebrow={t("exams.hero.eyebrow")}
        title={t("exams.hero.title")}
        lead={t("exams.hero.lead")}
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contato" size="lg">
            {t("common.bookDemo")}
          </Button>
          <WhatsAppButton variant="outline" className="border-white/25 text-white hover:bg-white/10" />
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <FeatureCard key={f.k} icon={f.icon} accent="exams" title={t(`exams.f.${f.k}.title`)} desc={t(`exams.f.${f.k}.desc`)} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Review mockup */}
      <Section alt>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Badge tone="exams">{t("exams.f.review.title")}</Badge>
              <h2 className="mt-4 text-3xl">{t("exams.f.review.title")}</h2>
              <p className="mt-4 text-muted-foreground">{t("exams.f.review.desc")}</p>
              <ul className="mt-6 space-y-3">
                {["timestamp", "ai", "modes"].map((k) => (
                  <li key={k} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-exams" />
                    <span className="text-sm">
                      <strong className="font-semibold">{t(`exams.f.${k}.title`)}</strong> — {t(`exams.f.${k}.desc`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <ReviewMockup />
          </div>
        </Container>
      </Section>

      {/* Compare */}
      <Section>
        <Container>
          <SectionHead center title={t("exams.compare.title")} />
          <Card className="mx-auto mt-10 max-w-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="px-5 py-3 text-left font-semibold"> </th>
                  <th className="px-5 py-3 text-center font-semibold text-muted-foreground">{t("exams.compare.without")}</th>
                  <th className="px-5 py-3 text-center font-semibold text-exams">{t("exams.compare.with")}</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map(([label, a, b], i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-5 py-3">{label}</td>
                    <td className="px-5 py-3 text-center">
                      {a ? <Check className="mx-auto h-4 w-4 text-success" /> : <X className="mx-auto h-4 w-4 text-muted-foreground/50" />}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {b ? <Check className="mx-auto h-4 w-4 text-exams" /> : <X className="mx-auto h-4 w-4 text-muted-foreground/50" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Container>
      </Section>

      {/* FAQ */}
      <Section alt>
        <Container>
          <SectionHead center title={t("exams.faq.title")} />
          <div className="mx-auto mt-10 max-w-2xl space-y-3">
            {faqs.map(([q, a], i) => (
              <Card key={i} className="overflow-hidden">
                <button
                  onClick={() => setFaq(faq === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-semibold">{q}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${faq === i ? "rotate-180" : ""}`} />
                </button>
                {faq === i && <p className="px-5 pb-4 text-sm text-muted-foreground">{a}</p>}
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
